import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import BackendService from '../services/backendService';
import { Template } from '../services/TemplateService/Template.type';
import useAccessBadge from './useAccessBadge';
import useAutosaveTemplate from './useAutosaveTemplate';
import { DesignerUIStateActions, useDesignerUIContext } from './useDesignerUIState';

export const useRawTemplate = (templateId?: string | null, templateTenantId?: string) => {
  const templateCacheKey = ['template', templateId];
  const dispatch = useDesignerUIContext(context => context.dispatch);

  const { data: template, isFetching: isFetchingTemplate, isError, error } = useQuery<
    Template | undefined,
    AxiosError
  >(
    templateCacheKey,
    async _ => {
      if (templateId) {
        if (templateTenantId) {
          return BackendService.instance.blueprint.retrieveTemplateAuthenticated(
            templateId,
            templateTenantId,
          ) as Promise<Template | undefined>;
        }
        return BackendService.instance.blueprint.retrieveTemplate(templateId) as Promise<
          Template | undefined
        >;
      }
      return Promise.resolve(undefined);
    },
    {
      enabled: Boolean(templateId),
      staleTime: 1000 * 60 * 5,
      onSuccess: () => {
        dispatch({
          type: DesignerUIStateActions.setForceLoadingState,
          payload: 'success',
        });
      },
      retry: (count: number, error) => {
        // due to SSR fetching we dont want to retry on 403s and 404s
        if (error.response?.status && [403, 404].includes(error.response?.status)) {
          return false;
        }

        // Default retry is 3 times
        if (count > 3) {
          return false;
        }
        return true;
      },
    },
  );
  return {
    template,
    isFetchingTemplate,
    isError,
    error,
  };
};

function useTemplate(templateId?: string | null, templateTenantId?: string) {
  const { accessBadge: userAccessBadge } = useAccessBadge();

  const { error, isError, isFetchingTemplate, template } = useRawTemplate(
    templateId,
    templateTenantId,
  );

  const isTemplateOwner = useMemo(
    () => template && userAccessBadge?.user.id === template?.creator.uid,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userAccessBadge, template, templateId],
  );

  const {
    autoSaveTemplate,
    isSavingTemplate,
    isSavingTemplateError,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
  } = useAutosaveTemplate();

  return {
    template,
    isFetchingTemplate,
    autoSaveTemplate,
    isSavingTemplate,
    isError,
    error,
    isTemplateOwner,
    isSavingTemplateError,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
  };
}
export default useTemplate;
